import { MouseEvent, RefObject, useRef, useState } from 'react';
import { IconButton, Stack, TableCell, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form, { FormDataModel, FormModel } from 'components/Form';
import { VarianceChip, VarianceQtyHint } from 'components/InputField';
import Tabular from 'components/Tabular/Tabular';
import { ToolTip } from 'components/Tooltip/ToolTip';
import { Validators } from 'utils/validators';
import { FormComponent } from 'utils/validators/HelperComponents';
import { formatCurrency, getDrawerWidth } from 'utils/validators/HelperFunctions';
import { getPricePackageName } from 'utils/pricePackage';
import { ActualizationTaxCell } from '../ActualizationTaxCell';
import { actualizationTableSxFor, lineItemColumnWidths, mobileLineItemColumnWidths } from '../actualizationTableLayout';

export type VenueEditableField = 'guaranteedGuests' | 'hallRental';

type VenueChargesTableProps = {
  preview: any;
  pageResponse: any;
  bookingLineItem: any;
  pastBooking: boolean;
  /** Whether Actual Guests / Hall Rental can be edited (same rule as the form fields). */
  editable?: boolean;
  getFieldValue?: (field: VenueEditableField) => any;
  onFieldChange?: (field: VenueEditableField, value: string) => void;
};

type VenueRow = {
  key: string;
  name: string;
  subtitle?: string;
  editField?: VenueEditableField;
  qty: number;
  qtyHints?: string[];
  pricing: string;
  pricingHint?: string;
  tax: number;
  taxGroupName?: string;
  amount: number;
  varianceAmount: number | null;
  varianceQty?: number;
};

const num = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const varianceTone = (delta: number) => (delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral');

const perPersonLabel = (rate: number) =>
  String(strings.formatString(strings.actualization_addon_rate_person, formatCurrency(rate)));

const buildRows = (preview: any, pageResponse: any, bookingLineItem: any, actualGuestsInput: unknown): VenueRow[] => {
  const calc = preview?.calculations;
  const variance = preview?.variance;
  const guests = calc?.guests;
  const isHourly = Boolean(bookingLineItem?.hourlyPricing ?? pageResponse?.isHourlyPricing);
  const scPercent = num(calc?.serviceChargePercentage ?? pageResponse?.serviceChargePercentage);
  const rows: VenueRow[] = [];

  if (!isHourly) {
    const ppp = num(pageResponse?.pricePerPerson);
    const billable = num(pageResponse?.billableGuests ?? pageResponse?.guaranteedGuests);
    const baseCount = guests ? num(guests.baseGuestCount) : billable;
    const excessCount = guests ? num(guests.excessGuestCount) : 0;
    const baseCharges = guests ? num(guests.baseGuestCharges) : ppp * billable;
    const excessCharges = guests ? num(guests.excessGuestCharges) : 0;
    const totalGuestCharges = baseCharges + excessCharges;
    const guestTax = num(guests?.taxOnGuestCharge);
    const excessRate = num(calc?.excessGuestRate ?? pageResponse?.excessGuestRate);
    const splitExcess = excessCount > 0 && excessRate > 0;
    const guestRowCount = splitExcess ? baseCount : baseCount + excessCount;
    const guestRowCharges = splitExcess ? baseCharges : totalGuestCharges;
    const guestRowTax = splitExcess && totalGuestCharges > 0 ? (guestTax * baseCharges) / totalGuestCharges : guestTax;
    const excessTax = guestTax - guestRowTax;
    const bookedGuests = num(variance?.guests?.bookedGuests ?? bookingLineItem?.guaranteedGuests);
    const bookedGuestCharges = variance?.guests?.bookedGuestCharges;
    const actualGuests = num(
      actualGuestsInput !== undefined && actualGuestsInput !== ''
        ? actualGuestsInput
        : guests?.guaranteedGuests ?? pageResponse?.guaranteedGuests,
    );

    rows.push({
      key: 'guest',
      name: strings.bookingDetails_text_guestCharges,
      subtitle: getPricePackageName(bookingLineItem, '').trim() || undefined,
      editField: 'guaranteedGuests',
      qty: guestRowCount,
      qtyHints: [
        String(strings.formatString(strings.actualization_actual_hint, actualGuests)),
        String(strings.formatString(strings.actualization_addon_booked_hint, bookedGuests)),
      ],
      pricing: perPersonLabel(guestRowCount > 0 ? guestRowCharges / guestRowCount : ppp),
      tax: guestRowTax,
      taxGroupName: guests?.taxGroupName,
      amount: guestRowCharges + (guestRowCharges * scPercent) / 100 + guestRowTax,
      varianceAmount: bookedGuestCharges == null ? null : guestRowCharges - num(bookedGuestCharges),
      varianceQty: guestRowCount - bookedGuests,
    });

    if (splitExcess) {
      rows.push({
        key: 'extra-guest',
        name: strings.actualization_extra_guest_charges,
        editField: 'guaranteedGuests',
        qty: excessCount,
        pricing: perPersonLabel(excessCharges / excessCount),
        tax: excessTax,
        taxGroupName: guests?.taxGroupName,
        amount: excessCharges + (excessCharges * scPercent) / 100 + excessTax,
        varianceAmount: excessCharges,
        varianceQty: excessCount,
      });
    }
  }

  const hallRental = num(calc?.hallRental ?? pageResponse?.hallRental);
  const bookedHall = num(variance?.hallRental?.booked ?? bookingLineItem?.hallRental);
  const hallTax = num(calc?.taxOnHallRental);
  rows.push({
    key: 'hall',
    name: isHourly ? strings.bookingDetails_text_totalRental : strings.bookingDetailsHallRental,
    subtitle: String(bookingLineItem?.venueName ?? '').trim() || undefined,
    editField: 'hallRental',
    qty: 1,
    pricing: formatCurrency(hallRental),
    pricingHint: String(strings.formatString(strings.actualization_addon_booked_hint, formatCurrency(bookedHall))),
    tax: hallTax,
    taxGroupName: calc?.hallRentalTaxGroupName,
    amount: hallRental + hallTax,
    varianceAmount: variance?.hallRental?.delta == null ? hallRental - bookedHall : num(variance.hallRental.delta),
  });

  return rows;
};

const editFormModel = (field: VenueEditableField): FormModel[] => [
  field === 'guaranteedGuests'
    ? {
        label: strings.booking_details_actual_revenue_number_of_guest,
        value: '',
        size: 'medium',
        type: 'text',
        typeValue: 'number',
        variant: 'outlined',
        placeholder: '',
        field,
        validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 100000 }],
        responsive: { xs: 12 },
        required: true,
      }
    : {
        label: strings.bookingDetailsHallRental,
        value: '',
        size: 'medium',
        type: 'text',
        typeValue: 'number',
        variant: 'outlined',
        placeholder: '',
        field,
        validators: [{ check: Validators.validateFloat }],
        responsive: { xs: 12 },
        required: false,
      },
];

export const VenueChargesTable = ({
  preview,
  pageResponse,
  bookingLineItem,
  pastBooking,
  editable = false,
  getFieldValue,
  onFieldChange,
}: VenueChargesTableProps) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const mobile = screenSizeDownSm;
  const [editingField, setEditingField] = useState<VenueEditableField | null>(null);
  const [drawerValues, setDrawerValues] = useState<any>({});
  const [hasError, setHasError] = useState(false);
  const formRef: RefObject<Form | null | undefined> = useRef();

  const rows = buildRows(preview, pageResponse, bookingLineItem, getFieldValue?.('guaranteedGuests'));
  const canEditRows = pastBooking && editable && Boolean(onFieldChange);

  const openEdit = (field: VenueEditableField) => {
    const current = getFieldValue ? getFieldValue(field) : pageResponse?.[field];
    setDrawerValues({ [field]: current ?? '' });
    setHasError(false);
    setEditingField(field);
  };

  const closeEdit = () => {
    setEditingField(null);
    setDrawerValues({});
    setHasError(false);
  };

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!editingField) return;
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    if (!isFormValid) {
      setHasError(true);
      return;
    }
    onFieldChange?.(editingField, String(formData[editingField] ?? ''));
    closeEdit();
  };

  const headers = [
    strings.description,
    strings.actualization_addon_qty,
    ...(mobile
      ? []
      : [strings.actualization_addon_pricing, strings.actualization_addon_discount, strings.actualization_addon_tax]),
    strings.actualization_addon_amount,
    ...(mobile ? [] : [strings.actualization_addon_variance]),
    ...(pastBooking ? [''] : []),
  ];
  const columnAlign = headers.map((_, i) => (i === 0 ? 'left' : 'right')) as Array<'left' | 'right'>;
  const totalAmount = rows.reduce((sum, row) => sum + row.amount, 0);

  return (
    <>
    <Tabular
      size="small"
      headerColor="secondary"
      rows={headers}
      columnAlign={columnAlign}
      columnWidths={mobile ? mobileLineItemColumnWidths(pastBooking) : lineItemColumnWidths(pastBooking)}
      sx={actualizationTableSxFor(mobile)}>
      {rows.map(row => {
        const rowEditable = canEditRows && Boolean(row.editField);
        return (
        <TableRow
          key={row.key}
          hover={rowEditable}
          onClick={rowEditable ? () => openEdit(row.editField as VenueEditableField) : undefined}
          sx={rowEditable ? { cursor: 'pointer' } : undefined}>
          <TableCell scope="row">
            <Typography sx={{ fontSize: '13px', lineHeight: 1.25, fontWeight: 600 }}>{row.name}</Typography>
            {row.subtitle ? (
              <Typography color="text.secondary" sx={{ fontSize: '11px', lineHeight: 1.25 }}>
                {row.subtitle}
              </Typography>
            ) : null}
          </TableCell>
          <TableCell align="right">
            <Stack alignItems="flex-end" spacing={0.125}>
              <Typography sx={{ fontSize: '14px', lineHeight: 1.25, fontWeight: 500 }}>{row.qty}</Typography>
              {row.qtyHints?.length ? (
                <Typography color="text.secondary" sx={{ fontSize: '10px', lineHeight: 1.25, whiteSpace: 'nowrap' }}>
                  {row.qtyHints.join(' · ')}
                </Typography>
              ) : null}
            </Stack>
          </TableCell>
          {!mobile && (
            <>
              <TableCell align="right">
                {row.pricingHint ? (
                  <Stack alignItems="flex-end" spacing={0.125}>
                    <Typography sx={{ fontSize: '12px', lineHeight: 1.25 }}>{row.pricing}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '10px', lineHeight: 1.25 }}>
                      {row.pricingHint}
                    </Typography>
                  </Stack>
                ) : (
                  row.pricing
                )}
              </TableCell>
              <TableCell align="right">—</TableCell>
              <TableCell align="right">
                <ActualizationTaxCell
                  amount={row.tax > 0 || row.taxGroupName ? row.tax : null}
                  taxGroupName={row.taxGroupName}
                />
              </TableCell>
            </>
          )}
          <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
          {!mobile && (
            <TableCell align="right">
              {row.varianceAmount == null || row.varianceAmount === 0 ? (
                '—'
              ) : (
                <Stack alignItems="flex-end" spacing={0.25}>
                  <VarianceChip
                    label={formatCurrency(Math.abs(row.varianceAmount))}
                    tone={varianceTone(row.varianceAmount)}
                  />
                  {row.varianceQty ? <VarianceQtyHint delta={row.varianceQty} /> : null}
                </Stack>
              )}
            </TableCell>
          )}
          {pastBooking && (
            <TableCell align="right">
              {rowEditable ? (
                <ToolTip title="Edit">
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    onClick={e => {
                      e.stopPropagation();
                      openEdit(row.editField as VenueEditableField);
                    }}
                    aria-label="edit"
                    color="primary">
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ToolTip>
              ) : null}
            </TableCell>
          )}
        </TableRow>
        );
      })}
      <TableRow
        sx={{
          '& td': {
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: 1.25,
            py: 0.75,
            px: 1,
            boxShadow: theme => `inset 0 1px 0 ${theme.palette.divider}`,
          },
        }}>
        <TableCell colSpan={mobile ? 2 : 5}>{strings.actualization_venue_total}</TableCell>
        <TableCell align="right">{formatCurrency(totalAmount)}</TableCell>
        {!mobile && <TableCell align="right" />}
        {pastBooking && <TableCell align="right" />}
      </TableRow>
    </Tabular>
    <DrawerMenu
      width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
      open={editingField !== null}
      handleClose={closeEdit}
      closeButton={true}
      title={strings.actualization_venue_charges}>
      {editingField ? (
        <FormComponent
          hasError={hasError}
          formRef={formRef}
          pageResponse={drawerValues}
          handleSave={handleSave}
          formModel={editFormModel(editingField)}
        />
      ) : null}
    </DrawerMenu>
    </>
  );
};
